import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const GA4Context = createContext();

// Use environment variable for API URL, fallback to localhost for development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/ga4';

export const useGA4 = () => {
  const context = useContext(GA4Context);
  if (!context) {
    throw new Error('useGA4 must be used within a GA4Provider');
  }
  return context;
};

export const GA4Provider = ({ children }) => {
  // Date range state
  const [dateRange, setDateRange] = useState({
    startDate: '30daysAgo',
    endDate: 'today'
  });

  // Comparison settings
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [comparisonType, setComparisonType] = useState('week'); // 'week' or 'month'

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [metrics, setMetrics] = useState(null);
  const [timeSeries, setTimeSeries] = useState([]);
  const [acquisition, setAcquisition] = useState(null);
  const [events, setEvents] = useState([]);
  const [technology, setTechnology] = useState(null);
  const [pages, setPages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [sources, setSources] = useState([]);
  const [comparison, setComparison] = useState(null);

  /**
   * Fetch main metrics
   */
  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/metrics?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`
      );

      if (!response.ok) throw new Error('Failed to fetch metrics');

      const data = await response.json();

      // Parse metrics from GA4 response
      if (data.rows && data.rows[0]) {
        const row = data.rows[0];
        setMetrics({
          activeUsers: parseInt(row.metricValues[0]?.value || 0),
          newUsers: parseInt(row.metricValues[1]?.value || 0),
          sessions: parseInt(row.metricValues[2]?.value || 0),
          avgSessionDuration: parseFloat(row.metricValues[3]?.value || 0),
          bounceRate: parseFloat(row.metricValues[4]?.value || 0),
          engagementRate: parseFloat(row.metricValues[5]?.value || 0)
        });
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
      throw err;
    }
  }, [dateRange]);

  /**
   * Fetch time series data
   */
  const fetchTimeSeries = useCallback(async (granularity = 'day') => {
    try {
      const response = await fetch(
        `${API_URL}/timeseries?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&granularity=${granularity}`
      );

      if (!response.ok) throw new Error('Failed to fetch time series');

      const data = await response.json();

      // Transform GA4 response to usable format
      const transformed = data.rows?.map(row => ({
        date: row.dimensionValues[0].value,
        hour: granularity === 'hour' ? row.dimensionValues[1]?.value : null,
        activeUsers: parseInt(row.metricValues[0]?.value || 0),
        newUsers: parseInt(row.metricValues[1]?.value || 0),
        sessions: parseInt(row.metricValues[2]?.value || 0),
        pageViews: parseInt(row.metricValues[3]?.value || 0),
        avgSessionDuration: parseFloat(row.metricValues[4]?.value || 0),
        engagementRate: parseFloat(row.metricValues[5]?.value || 0),
        eventCount: parseInt(row.metricValues[6]?.value || 0)
      })) || [];

      setTimeSeries(transformed);
    } catch (err) {
      console.error('Error fetching time series:', err);
      throw err;
    }
  }, [dateRange]);

  /**
   * Fetch acquisition data (channels, sources, campaigns)
   */
  const fetchAcquisition = useCallback(async (limit = 20) => {
    try {
      const response = await fetch(
        `${API_URL}/acquisition?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&limit=${limit}`
      );

      if (!response.ok) throw new Error('Failed to fetch acquisition data');

      const data = await response.json();

      const transformed = data.rows?.map(row => ({
        channel: row.dimensionValues[0].value,
        source: row.dimensionValues[1].value,
        medium: row.dimensionValues[2].value,
        sessions: parseInt(row.metricValues[0]?.value || 0),
        users: parseInt(row.metricValues[1]?.value || 0),
        newUsers: parseInt(row.metricValues[2]?.value || 0),
        engagementRate: parseFloat(row.metricValues[3]?.value || 0),
        avgSessionDuration: parseFloat(row.metricValues[4]?.value || 0)
      })) || [];

      setAcquisition(transformed);
    } catch (err) {
      console.error('Error fetching acquisition:', err);
      throw err;
    }
  }, [dateRange]);

  /**
   * Fetch events data
   */
  const fetchEvents = useCallback(async (limit = 20) => {
    try {
      const response = await fetch(
        `${API_URL}/events?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&limit=${limit}`
      );

      if (!response.ok) throw new Error('Failed to fetch events');

      const data = await response.json();

      const transformed = data.rows?.map(row => ({
        eventName: row.dimensionValues[0].value,
        eventCount: parseInt(row.metricValues[0]?.value || 0)
      })) || [];

      setEvents(transformed);
    } catch (err) {
      console.error('Error fetching events:', err);
      throw err;
    }
  }, [dateRange]);

  /**
   * Fetch technology data
   */
  const fetchTechnology = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/technology?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`
      );

      if (!response.ok) throw new Error('Failed to fetch technology data');

      const data = await response.json();

      const transformed = data.rows?.map(row => ({
        deviceCategory: row.dimensionValues[0].value,
        browser: row.dimensionValues[1].value,
        os: row.dimensionValues[2].value,
        users: parseInt(row.metricValues[0]?.value || 0),
        sessions: parseInt(row.metricValues[1]?.value || 0),
        avgSessionDuration: parseFloat(row.metricValues[2]?.value || 0),
        bounceRate: parseFloat(row.metricValues[3]?.value || 0)
      })) || [];

      setTechnology(transformed);
    } catch (err) {
      console.error('Error fetching technology:', err);
      throw err;
    }
  }, [dateRange]);

  /**
   * Fetch pages data
   */
  const fetchPages = useCallback(async (limit = 20) => {
    try {
      const response = await fetch(
        `${API_URL}/pages/detailed?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&limit=${limit}`
      );

      if (!response.ok) throw new Error('Failed to fetch pages');

      const data = await response.json();

      const transformed = data.rows?.map(row => ({
        pagePath: row.dimensionValues[0].value,
        pageTitle: row.dimensionValues[1]?.value || '',
        pageViews: parseInt(row.metricValues[0]?.value || 0),
        avgSessionDuration: parseFloat(row.metricValues[1]?.value || 0),
        engagementRate: parseFloat(row.metricValues[2]?.value || 0)
      })) || [];

      setPages(transformed);
    } catch (err) {
      console.error('Error fetching pages:', err);
      throw err;
    }
  }, [dateRange]);

  /**
   * Fetch countries data
   */
  const fetchCountries = useCallback(async (limit = 10) => {
    try {
      const response = await fetch(
        `${API_URL}/countries?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&limit=${limit}`
      );

      if (!response.ok) throw new Error('Failed to fetch countries');

      const data = await response.json();

      const transformed = data.rows?.map(row => ({
        country: row.dimensionValues[0].value,
        users: parseInt(row.metricValues[0]?.value || 0)
      })) || [];

      setCountries(transformed);
    } catch (err) {
      console.error('Error fetching countries:', err);
      throw err;
    }
  }, [dateRange]);

  /**
   * Fetch sources data
   */
  const fetchSources = useCallback(async (limit = 10) => {
    try {
      const response = await fetch(
        `${API_URL}/sources?limit=${limit}`
      );

      if (!response.ok) throw new Error('Failed to fetch sources');

      const data = await response.json();

      const transformed = data.rows?.map(row => ({
        source: row.dimensionValues[0].value,
        users: parseInt(row.metricValues[0]?.value || 0),
        sessions: parseInt(row.metricValues[1]?.value || 0)
      })) || [];

      setSources(transformed);
    } catch (err) {
      console.error('Error fetching sources:', err);
      throw err;
    }
  }, []);

  /**
   * Fetch comparison data
   */
  const fetchComparison = useCallback(async (metric = 'activeUsers') => {
    if (!comparisonEnabled) return;

    try {
      const response = await fetch(
        `${API_URL}/comparison?comparisonType=${comparisonType}&metric=${metric}`
      );

      if (!response.ok) throw new Error('Failed to fetch comparison data');

      const data = await response.json();
      setComparison(data);
    } catch (err) {
      console.error('Error fetching comparison:', err);
      throw err;
    }
  }, [comparisonEnabled, comparisonType]);

  /**
   * Fetch all GA4 data
   */
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchMetrics(),
        fetchTimeSeries(),
        fetchAcquisition(),
        fetchEvents(),
        fetchTechnology(),
        fetchPages(),
        fetchCountries(),
        fetchSources()
      ]);

      if (comparisonEnabled) {
        await fetchComparison();
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching GA4 data:', err);
    } finally {
      setLoading(false);
    }
  }, [
    fetchMetrics,
    fetchTimeSeries,
    fetchAcquisition,
    fetchEvents,
    fetchTechnology,
    fetchPages,
    fetchCountries,
    fetchSources,
    fetchComparison,
    comparisonEnabled
  ]);

  /**
   * Refresh all data
   */
  const refresh = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  /**
   * Update date range
   */
  const updateDateRange = useCallback((newRange) => {
    setDateRange(newRange);
  }, []);

  /**
   * Toggle comparison
   */
  const toggleComparison = useCallback((enabled) => {
    setComparisonEnabled(enabled);
  }, []);

  /**
   * Set comparison type
   */
  const updateComparisonType = useCallback((type) => {
    setComparisonType(type);
  }, []);

  // Fetch data on mount and when date range changes
  useEffect(() => {
    fetchAllData();
  }, [dateRange, comparisonEnabled, comparisonType]);

  const value = {
    // State
    dateRange,
    comparisonEnabled,
    comparisonType,
    loading,
    error,

    // Data
    metrics,
    timeSeries,
    acquisition,
    events,
    technology,
    pages,
    countries,
    sources,
    comparison,

    // Actions
    refresh,
    updateDateRange,
    toggleComparison,
    updateComparisonType,
    fetchTimeSeries,
    fetchAcquisition,
    fetchEvents,
    fetchTechnology,
    fetchPages,
    fetchCountries,
    fetchSources,
    fetchComparison
  };

  return <GA4Context.Provider value={value}>{children}</GA4Context.Provider>;
};
